// Thêm / sửa một địa chỉ trong sổ địa chỉ giao hàng của khách. Ô nhập dùng lại SmartAddressFields
// (dán nhanh, bản đồ, tỉnh / quận / phường) như địa chỉ gửi hàng.

import { useEffect, useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { patch, post, ApiError } from "@/lib/api"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { SmartAddressFields } from "@/components/SmartAddressFields"
import type { SavedAddress } from "./types"

export interface AddressDraft {
  street: string
  city: string
  district: string | null
  ward: string
  lat: number | null
  lng: number | null
  label: string
  isDefault: boolean
}

export const emptyAddressDraft = (isDefault = false): AddressDraft => ({ street: "", city: "", district: null, ward: "", lat: null, lng: null, label: "", isDefault })

const draftFrom = (a: SavedAddress): AddressDraft => ({
  street: a.street,
  city: a.city ?? "",
  district: a.district,
  ward: a.ward ?? "",
  lat: a.lat,
  lng: a.lng,
  label: a.label ?? "",
  isDefault: a.isDefault,
})

/** Thiếu gì để lưu được — null là đủ (khớp customerAddressInputSchema). */
export function draftError(d: AddressDraft): string | null {
  if (d.street.trim().length < 3) return "Nhập số nhà, tên đường"
  if (!d.city.trim()) return "Chọn tỉnh / thành phố"
  if (!d.ward.trim()) return "Chọn phường / xã"
  return null
}

export const draftPayload = (d: AddressDraft) => ({
  street: d.street.trim(),
  city: d.city.trim(),
  district: d.district?.trim() || null,
  ward: d.ward.trim(),
  lat: d.lat,
  lng: d.lng,
  label: d.label.trim() || null,
  isDefault: d.isDefault,
})

export function AddressDraftFields({
  value,
  onChange,
  disabled,
  showDefault = true,
}: {
  value: AddressDraft
  onChange: (next: AddressDraft) => void
  disabled?: boolean
  showDefault?: boolean
}) {
  return (
    <div className="space-y-3">
      <SmartAddressFields
        value={{ senderAddress: value.street, senderCity: value.city, senderDistrict: value.district, senderWard: value.ward, lat: value.lat, lng: value.lng }}
        onChange={(p) =>
          onChange({
            ...value,
            street: p.senderAddress ?? value.street,
            city: p.senderCity ?? value.city,
            district: p.senderDistrict !== undefined ? p.senderDistrict || null : value.district,
            ward: p.senderWard ?? value.ward,
            lat: p.lat !== undefined ? p.lat ?? null : value.lat,
            lng: p.lng !== undefined ? p.lng ?? null : value.lng,
          })
        }
        disabled={disabled}
      />
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex min-w-0 flex-1 items-center gap-2 text-sm text-inkSoft">
          <span className="shrink-0">Ghi nhớ là</span>
          <Input
            value={value.label}
            onChange={(e) => onChange({ ...value, label: e.target.value })}
            placeholder="Nhà, Công ty…"
            maxLength={50}
            disabled={disabled}
            className="h-11 desktop:h-9 bg-paper"
          />
        </label>
        {showDefault && (
          <label className="inline-flex min-h-11 desktop:min-h-0 cursor-pointer items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              className="size-4 accent-accent"
              checked={value.isDefault}
              onChange={(e) => onChange({ ...value, isDefault: e.target.checked })}
              disabled={disabled}
            />
            Đặt làm địa chỉ mặc định
          </label>
        )}
      </div>
    </div>
  )
}

export function CustomerAddressDialog({
  customerId,
  address,
  open,
  onOpenChange,
}: {
  customerId: string
  /** null = thêm mới */
  address: SavedAddress | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const qc = useQueryClient()
  const [draft, setDraft] = useState<AddressDraft>(emptyAddressDraft())
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    if (!open) return
    setDraft(address ? draftFrom(address) : emptyAddressDraft())
    setError(null)
  }, [open, address])

  const save = useMutation({
    mutationFn: () => {
      const body = draftPayload(draft)
      return address
        ? patch(`/customers/${customerId}/addresses/${address.id}`, body)
        : post(`/customers/${customerId}/addresses`, body)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["customers"] })
      toast.success(address ? "Đã lưu địa chỉ" : "Đã thêm địa chỉ")
      onOpenChange(false)
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === "customer_address.exists") setError("Địa chỉ này đã có trong sổ của khách")
      else setError((e as Error).message)
    },
  })

  return (
    <Dialog open={open} onOpenChange={(v) => !save.isPending && onOpenChange(v)}>
      <DialogContent className="sm:max-w-2xl max-h-[92dvh] overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{address ? "Sửa địa chỉ" : "Thêm địa chỉ"}</DialogTitle>
        </DialogHeader>
        <AddressDraftFields value={draft} onChange={setDraft} disabled={save.isPending} />
        {error && <p className="text-sm text-danger">{error}</p>}
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" className="h-11 desktop:h-9" disabled={save.isPending} onClick={() => onOpenChange(false)}>
            Hủy
          </Button>
          <Button
            type="button"
            className="h-11 desktop:h-9"
            disabled={save.isPending}
            onClick={() => {
              const err = draftError(draft)
              if (err) return setError(err)
              save.mutate()
            }}
          >
            {save.isPending ? "Đang lưu…" : "Lưu"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
